import type { CSSProperties } from 'react';
import { Flex } from '@nula/core-ui';
import { useCollectionDocuments, type CollectionDocSummary } from '../hooks/use-collection-documents';
import { seededRandom } from '../utils/seeded-random';
import styles from './collection-preview-stack.module.css';

interface Props {
  collectionId: string;
}

/** Max paper sheets fanned in the preview (and ghost sheets when empty). */
const MAX_TILES = 3;

type Kind = CollectionDocSummary['kind'];

/**
 * Seeded slot layout for one sheet. Each sheet has a gathered "rest" transform
 * (small overlap toward centre) and a "spread" transform applied on card hover
 * via CSS. The seeded rng adds tiny rotation jitter so two collections with the
 * same count don't look identical. Consumed in order so the rng stream is stable.
 *
 * Slots, by index: 0 = left, 1 = centre/front, 2 = right.
 */
function sheetStyle(rng: () => number, index: number, total: number): CSSProperties {
  // Per-sheet rest jitter — a few degrees around the slot's base angle.
  const jitter = Math.round((rng() - 0.5) * 8); // ~ -4°..4°

  // Base slot geometry. Single sheet sits centred; otherwise left/centre/right.
  const isLeft = index === 0 && total > 1;
  const isRight = index === total - 1 && total > 1;

  const restDx = isLeft ? -14 : isRight ? 14 : 0;
  const restAngle = (isLeft ? -7 : isRight ? 7 : 0) + jitter;
  const spreadDx = isLeft ? -34 : isRight ? 34 : 0;
  const spreadAngle = (isLeft ? -15 : isRight ? 15 : 0) + jitter;

  return {
    '--sheet-rest': `translate(${restDx}%, 3%) rotate(${restAngle}deg)`,
    '--sheet-spread': `translate(${spreadDx}%, 1%) rotate(${spreadAngle}deg)`,
    '--sheet-z': String(index === 1 ? total : index),
  } as CSSProperties;
}

/** Abstract, white-sheet content per kind — shape only, no kind tint. */
function SheetContent({ kind }: { kind: Kind }) {
  if (kind === 'canvas') {
    // Scattered sticky notes — the one kind that carries colour (the notes).
    return (
      <span className={styles.canvas} aria-hidden="true">
        <span className={`${styles.sticky} ${styles.sticky1}`} data-testid="canvas-sticky" />
        <span className={`${styles.sticky} ${styles.sticky2}`} data-testid="canvas-sticky" />
        <span className={`${styles.sticky} ${styles.sticky3}`} data-testid="canvas-sticky" />
      </span>
    );
  }
  // document — a few text lines of varying width.
  return (
    <span className={styles.document} aria-hidden="true">
      <span className={`${styles.line} ${styles.lineTitle}`} data-testid="doc-line" />
      <span className={`${styles.line} ${styles.lineM}`} data-testid="doc-line" />
      <span className={styles.line} data-testid="doc-line" />
      <span className={`${styles.line} ${styles.lineS}`} data-testid="doc-line" />
    </span>
  );
}

/** Seeded in-place shuffle (Fisher–Yates) so kind order in the fan is stable per
 *  collection but looks randomized. */
function seededShuffle<T>(items: T[], rng: () => number): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Preview content for a collection's PageCard: an animated fan of up to
 * MAX_TILES white "paper" sheets. The sheets are gathered at rest and spread
 * outward when the surrounding card is hovered (CSS-driven; disabled under
 * prefers-reduced-motion). Each sheet's shape content signals the kind of a real
 * document in the collection (document / canvas). Empty collections
 * show the same fan as faint dashed ghost sheets.
 *
 * Layout (kind order + jitter) is seeded by the collection id, so it's
 * randomized-looking but stable across renders.
 */
export function CollectionPreviewStack({ collectionId }: Props) {
  const docs = useCollectionDocuments(collectionId);
  const rng = seededRandom(collectionId);

  if (docs.length === 0) {
    return (
      <Flex align="center" justify="center" className={styles.collage}>
        {Array.from({ length: MAX_TILES }, (_, i) => (
          <div
            key={i}
            className={styles.ghostSheet}
            style={sheetStyle(rng, i, MAX_TILES)}
            data-testid="collection-ghost-tile"
            aria-hidden="true"
          />
        ))}
      </Flex>
    );
  }

  // Take up to MAX_TILES, then shuffle their order with the seeded rng so which
  // kind sits left/centre/right is stable-but-randomized per collection.
  const chosen = seededShuffle(docs.slice(0, MAX_TILES), rng);

  return (
    <Flex align="center" justify="center" className={styles.collage}>
      {chosen.map((d, i) => (
        <div
          key={d.id}
          className={styles.sheet}
          style={sheetStyle(rng, i, chosen.length)}
          data-testid="collection-tile"
          data-kind={d.kind}
        >
          <SheetContent kind={d.kind} />
        </div>
      ))}
    </Flex>
  );
}
